import React from 'react'
import { useTable, EditButton, DeleteButton, CreateButton } from '@refinedev/antd'
import { Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, EmptyHint, type Stat } from '../../theme/listkit'
import { Trophy } from '../../components/icons'

type HonorYear = Tables<'honor_years'>

/** A single honor year: bordered card, Cinzel year, inline Edit/Delete (PK is `year`). */
function HonorYearCard({ year }: { year: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '14px 16px',
        borderRadius: 12,
        background: '#fff',
        border: '1px solid ' + brand.cardLine,
        borderLeft: '3px solid ' + brand.gold,
      }}
    >
      <span
        style={{
          fontFamily: brand.fDisplay,
          fontWeight: 700,
          fontSize: 14,
          color: brand.ink,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {year}
      </span>
      <Space size={2} style={{ flexShrink: 0 }}>
        <EditButton hideText size="small" recordItemId={year} />
        <DeleteButton hideText size="small" recordItemId={year} />
      </Space>
    </div>
  )
}

export function HonorYearList() {
  // useTable establishes the `honor_years` resource context (so the intro
  // <CreateButton/> resolves) and loads the rows; we render our own chip grid
  // instead of a table. Honor years are a small, domain-bounded set — cap the
  // page at 200 (explicit bound instead of mode:'off') so the whole grid renders.
  const { tableProps } = useTable<HonorYear>({
    syncWithLocation: true,
    pagination: { pageSize: 200 },
  })
  const honorYears = (tableProps.dataSource ?? []) as HonorYear[]

  const stats: Stat[] = [
    { label: 'Honor Years', value: honorYears.length.toLocaleString(), icon: Trophy, tone: 'gold' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Giving · Honor Years',
        description:
          'The milestone reunion classes celebrated at Homecoming (the 5-year cohorts: 1991, 1996, 2001, …). These power the Honor Years strip on the app home screen.',
        actions: <CreateButton />,
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Milestone Classes" sub="Reunion years featured on the app home screen">
        {honorYears.length === 0 ? (
          <EmptyHint>No honor years yet. Add the first milestone class.</EmptyHint>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 12,
            }}
          >
            {honorYears.map((hy) => (
              <HonorYearCard key={String(hy.year)} year={hy.year} />
            ))}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
